import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export interface RealtimeResume {
  resumeId: string;
  filename: string;
  candidateAlias?: string;
  displayName?: string;
  filenames?: string[];
  uploadedAt: string;
  uploadedBy?: string;
  fileSize: number;
  fileType: string;
  status: string;
  rejectionReason?: string;
  rejectionDetail?: string;
  failureReason?: string;
  error?: string;
  pageCount?: number;
  jdIds?: string[];
  tags?: string[];
  placedInJobId?: string;
  inlineScores?: Record<string, { score: number; summary: string; scoredAt: string; dimensionScores?: Record<string, number> }>;
}

/**
 * Real-time Firestore listener for the org's resumes.
 * Returns ALL resumes (client-side pagination/search handled by consumer).
 */
export function useRealtimeResumes(orgId: string | undefined) {
  const [resumes, setResumes] = useState<RealtimeResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setResumes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'resumes'),
      where('orgId', '==', orgId),
      orderBy('uploadedAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          // uploadedAt may be Firestore Timestamp or ISO string
          let uploadedAt = '';
          if (d.uploadedAt?.toDate) {
            uploadedAt = d.uploadedAt.toDate().toISOString();
          } else if (typeof d.uploadedAt === 'string') {
            uploadedAt = d.uploadedAt;
          }

          return {
            resumeId: doc.id,
            filename: d.filename ?? '',
            candidateAlias: d.candidateAlias ?? undefined,
            displayName: d.displayName ?? undefined,
            filenames: d.filenames,
            uploadedAt,
            uploadedBy: typeof d.uploadedBy === 'string' ? d.uploadedBy : undefined,
            fileSize: d.fileSize ?? 0,
            fileType: d.fileType ?? '',
            status: d.status ?? 'uploaded',
            rejectionReason: typeof d.rejectionReason === 'string' ? d.rejectionReason : undefined,
            rejectionDetail: typeof d.rejectionDetail === 'string' ? d.rejectionDetail : undefined,
            failureReason: typeof d.failureReason === 'string' ? d.failureReason : undefined,
            error: typeof d.error === 'string' ? d.error : undefined,
            pageCount: typeof d.pageCount === 'number' ? d.pageCount : undefined,
            jdIds: d.jdIds ?? [],
            tags: d.tags ?? [],
            placedInJobId: d.placedInJobId ?? undefined,
            inlineScores: d.inlineScores,
          };
        });
        setResumes(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Realtime resumes listener error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [orgId]);

  return { resumes, loading, error };
}
